import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import Quote from "components/card/quote/Quote";
import MLTAQuoteStart from "components/card/mlta-quote-start/MLTAQuoteStart";
import { useApplicationForm, useMLTAApplicationForm } from "hooks/useApplicationForm";
import { useDTOQuoteForm } from "hooks/useDTOQuoteForm";
import { useMLTAQuoteForm } from "hooks/useMLTAQuoteForm";
import { useProductContext } from "context/ProductContext";
import type { QuoteRef } from "types/Card";
import { useSessionId } from "hooks/useSessionId";

const QuotePage = () => {
    const { isMLTA, isDTO, isGIWL } = useProductContext();
    const { processSessionId } = useSessionId();
    const dtoForm = useApplicationForm();
    const mltaForm = useMLTAApplicationForm();
    const quoteRef = useRef<QuoteRef>(null);

    // ========== Effects ==========
    // Create sessionID when quote page renders
    useEffect(() => {
        processSessionId();
    }, [processSessionId]);

    // Watch quote form data for respective forms
    const dtoQuoteFormData = useWatch({
        control: dtoForm.control,
        name: "quote",
    });

    const mltaQuoteFormData = useWatch({
        control: mltaForm.control,
        name: "quote",
    });

    // Call hooks unconditionally (React rules of hooks)
    const dtoQuoteFormState = useDTOQuoteForm({ form: dtoForm, quoteFormData: dtoQuoteFormData });
    const mltaQuoteFormState = useMLTAQuoteForm({ form: mltaForm, quoteFormData: mltaQuoteFormData });

    // Create date of birth blur handlers
    const handleDTODateOfBirthBlur = () => {
        dtoForm.trigger("quote.dateOfBirth");
    };

    const handleMLTADateOfBirthBlur = () => {
        mltaForm.trigger("quote.dateOfBirth");
    };

    return (
        <>
            <title>Quote | AAA Life</title>
            {(isDTO || isGIWL) && (
                <Quote
                    ref={quoteRef}
                    form={dtoForm}
                    activeTab={dtoQuoteFormState.activeTab}
                    coverageOptions={dtoQuoteFormState.coverageOptions}
                    selectedCoverage={dtoQuoteFormState.selectedCoverage}
                    isQuoteFormValid={dtoQuoteFormState.isQuoteFormValid}
                    isLoadingQuotes={dtoQuoteFormState.isLoadingQuotes}
                    offerCoverageAmounts={dtoQuoteFormState.offerCoverageAmounts}
                    memberOfferAvailable={dtoQuoteFormState.memberOfferAvailable}
                    spouseOfferAvailable={dtoQuoteFormState.spouseOfferAvailable}
                    currentPremium={dtoQuoteFormState.currentPremium}
                    displayName={dtoQuoteFormState.displayName}
                    isNewYorkSelected={dtoQuoteFormState.isNewYorkSelected}
                    dobMonth={dtoQuoteFormState.dobMonth}
                    dobDay={dtoQuoteFormState.dobDay}
                    dobYear={dtoQuoteFormState.dobYear}
                    gender={dtoQuoteFormState.gender}
                    stateValue={dtoQuoteFormState.stateValue}
                    nicotineUsage={dtoQuoteFormState.nicotineUsage}
                    onTabChange={dtoQuoteFormState.handleTabChange}
                    onCoverageChange={dtoQuoteFormState.handleCoverageChange}
                    onApplyNow={dtoQuoteFormState.handleApplyNow}
                    onDateOfBirthBlur={handleDTODateOfBirthBlur}
                />
            )}

            {isMLTA && (
                <MLTAQuoteStart
                    form={mltaForm}
                    dobMonth={mltaQuoteFormState.dobMonth}
                    dobDay={mltaQuoteFormState.dobDay}
                    dobYear={mltaQuoteFormState.dobYear}
                    stateValue={mltaQuoteFormState.stateValue}
                    isUnavailableState={mltaQuoteFormState.isUnavailableState}
                    onSubmit={mltaQuoteFormState.handleSubmit}
                    onDateOfBirthBlur={handleMLTADateOfBirthBlur}
                    isLoading={mltaQuoteFormState.isLoadingQuotes}
                    firstName={mltaQuoteFormState.firstName}
                />
            )}
        </>
    );
};

export default QuotePage;
